import type { TemplateSummary } from '@/features/page-editor/services/template-loader'
import { FileText, Layers, Eye, Plus } from 'lucide-react'
import { TierBadge } from './tier-badge'
import { AppThumbnailPreview } from './app-thumbnail-preview'
import { useStudioLocale } from '@/i18n'
import {
  localizeTemplateDescription,
  localizeTemplateDomain,
  localizeTemplateTag,
} from '@/features/project/lib/template-description-copy'

interface TemplateCardProps {
  template: TemplateSummary
  onOpen: (id: string) => void
  onUseTemplate?: (template: TemplateSummary) => void
}

export function TemplateCard({ template, onOpen, onUseTemplate }: TemplateCardProps) {
  const copy = useStudioLocale().strings.starters
  // The description, the domain chip and the tag chips are the catalog's
  // words, not this component's — see `template-description-copy.ts` for why
  // they are translated by parsing the catalog rather than by rewriting it.
  const catalog = copy.catalog
  return (
    <article
      data-testid="studio-template-card"
      className="group text-left border border-border rounded-xl p-4 hover:border-foreground/20 hover:bg-muted/30 transition-all"
    >
      <button
        type="button"
        onClick={() => onOpen(template.id)}
        className="block w-full text-left"
      >
        {/* Preview thumbnail */}
        <AppThumbnailPreview
          title={template.name}
          imageSrc={template.thumbnail}
          kind="template"
          className="mb-3"
        />

        <div className="flex items-center gap-1.5">
          {/* truncate hides the tail; title keeps the full name recoverable on hover */}
          <h3 className="text-sm font-medium transition-colors truncate" title={template.name}>
            {template.name}
          </h3>
          {template.tier && <TierBadge tier={template.tier} />}
        </div>
        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 min-h-[2rem]">
          {localizeTemplateDescription(template.description, catalog)}
        </p>

        <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {copy.gallery.pages(template.pages)}
          </span>
          <span className="flex items-center gap-1">
            <Layers className="w-3 h-3" />
            {copy.gallery.blocks(template.blocks.length)}
          </span>
        </div>
      </button>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mt-2">
        <span className="text-[10px] bg-sky-500/10 text-sky-700 rounded px-1.5 py-0.5">
          {localizeTemplateDomain(template.domain, catalog)}
        </span>
        {template.category && template.category !== template.domain && (
          <span className="text-[10px] bg-muted text-muted-foreground rounded px-1.5 py-0.5">
            {localizeTemplateTag(template.category, catalog)}
          </span>
        )}
        {template.tags.slice(0, 2).map((tag) => (
          <span key={tag} className="text-[10px] bg-muted text-muted-foreground rounded px-1.5 py-0.5">
            {localizeTemplateTag(tag, catalog)}
          </span>
        ))}
      </div>

      {/* Action buttons — always visible */}
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          aria-label={copy.gallery.previewAria(template.name)}
          onClick={(e) => { e.stopPropagation(); onOpen(template.id) }}
          className="flex h-10 flex-1 items-center justify-center gap-1 rounded-xl border border-border px-3 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Eye className="h-4 w-4" /> {copy.gallery.preview}
        </button>
        {onUseTemplate && (
          <button
            type="button"
            aria-label={copy.gallery.createFromAria(template.name)}
            onClick={(e) => { e.stopPropagation(); onUseTemplate(template) }}
            className="flex h-10 flex-1 items-center justify-center gap-1 rounded-xl bg-foreground px-3 text-xs text-background transition-colors hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> {copy.gallery.create}
          </button>
        )}
      </div>
    </article>
  )
}
